import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Copy } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../../../components/ui/dialog';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Textarea } from '../../../../components/ui/textarea';
import { Label } from '../../../../components/ui/label';
import { RootState } from '../../../../reducers';
import { shareThunk } from '../../selectedVideoSlice';
import { getEmbedUrl } from '../../../../lib/routingUtils';

interface EmbedModalProps {
  onClose: () => void;
}

const EmbedModal: React.FC<EmbedModalProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const { selectedVideo } = useSelector((state: RootState) => state);
  const {
    file,
    actions: {
      share: { status },
    },
  } = selectedVideo;
  const { _id, share: shareDetails } = file;
  const { shareId } = shareDetails || {};
  const [copied, setCopied] = useState(false);
  const [width, setWidth] = useState('100%');
  const [height, setHeight] = useState('960');

  const dispatch = useDispatch();
  useEffect(() => {
    if (!shareId) {
      dispatch(
        shareThunk({
          fileId: _id,
          copyUrlToClipboard: false,
        }),
      );
    }
  }, []);

  const generateEmbedCode = (w: string, h: string) => {
    if (!shareId) {
      return t('selectedVideo.actions.modals.embed.generating');
    }
    if (status === 'failed') {
      return t('selectedVideo.actions.modals.embed.failed');
    }
    return `<iframe src="${getEmbedUrl(
      shareId || '',
    )}?embed=true" width="${w}" height="${h}" frameborder="0" allowfullscreen></iframe>`;
  };

  const [embedCode, setEmbedCode] = useState(
    t('selectedVideo.actions.modals.embed.generating'),
  );

  useEffect(() => {
    setEmbedCode(generateEmbedCode(width, height));
  }, [shareId]);

  const handleCopy = () => {
    navigator.clipboard.writeText(embedCode).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleDimensionChange = (
    dimension: 'width' | 'height',
    value: string,
  ) => {
    if (dimension === 'width') {
      setWidth(value);
    } else {
      setHeight(value);
    }
    setEmbedCode(
      generateEmbedCode(
        dimension === 'width' ? value : width,
        dimension === 'height' ? value : height,
      ),
    );
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t('selectedVideo.actions.modals.embed.title')}
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4 py-4">
          <p className="text-sm text-muted-foreground">
            {t('selectedVideo.actions.modals.embed.description')}
          </p>
          <Textarea
            value={embedCode}
            readOnly
            rows={4}
            className="resize-none rounded-lg"
          />
          <div>
            <p className="mb-2 text-sm text-muted-foreground">
              {t('selectedVideo.actions.modals.embed.customDimensions')}:
            </p>
            <div className="flex gap-2">
              <div className="flex-1">
                <Label htmlFor="width" className="text-xs">
                  {t('selectedVideo.actions.modals.embed.width')}
                </Label>
                <Input
                  id="width"
                  value={width}
                  onChange={(e) =>
                    handleDimensionChange('width', e.target.value)
                  }
                  className="mt-1"
                />
              </div>
              <div className="flex-1">
                <Label htmlFor="height" className="text-xs">
                  {t('selectedVideo.actions.modals.embed.height')}
                </Label>
                <Input
                  id="height"
                  type="number"
                  value={height}
                  onChange={(e) =>
                    handleDimensionChange('height', e.target.value)
                  }
                  className="mt-1"
                />
              </div>
            </div>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button onClick={onClose} variant="outline">
            {t('common.cancel')}
          </Button>
          <Button onClick={handleCopy} className="gap-2">
            <Copy className="h-4 w-4" />
            {copied
              ? t('selectedVideo.actions.modals.embed.copied')
              : t('selectedVideo.actions.modals.embed.copy')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EmbedModal;
